<template>
    <FormSelectTree
        ref="FormTreeSelect"
        :options="options"
        :load-options="loadOptions"
        :normalizer="normalizer"
        async
        default-options
        v-bind="$attrs"
        v-on="$listeners"
    >
        <template #option-label="{ node }">
            <div class="d-flex align-items-center" @mousedown.stop.prevent>
                <strong>{{ node.label }}</strong>
                <div v-if="getDisabled(node)" class="m-1 p-0 width-25 height-25 ml-auto">
                    <feather type="check" class="text-success" />
                </div>
                <ButtonIcon
                    v-else
                    icon="plus"
                    :loading="getLoading(node)"
                    hide-label-while-loading
                    variant="success"
                    pill
                    class="m-1 p-0 width-25 height-25 ml-auto"
                    :disabled="disabled || anyLoading"
                    @mousedown.native.stop.prevent
                    @click.stop.prevent="handleClick(node)"
                />
            </div>
        </template>
    </FormSelectTree>
</template>



<script>
    import debounce from 'lodash/debounce';
    import { recursiveMapByKey } from '@/utils/util';
    import FormSelectTree from './FormSelectTree.vue';

    export default {
        name: 'FormCategoryProductTreeSelect',
        components: {
            FormSelectTree,
        },
        props: {
            groupId: {
                type: Number,
                required: true,
            },
            disabledNodes: {
                type: Array,
                default: () => [],
            },
            disabled: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                requestProductsTree: this.$req('space/ProductsTree'),
                requestProductsTreeSearch: this.$req('space/ProductsTreeSearch'),
                requestGroupProducts: this.$req('producerProductGroups/GroupProducts'),
                requestGroupCategories: this.$req('producerProductGroups/GroupCategories'),
                searchDebounced: null,
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            anyLoading() {
                return this.requestGroupCategories.getAnyLoading() || this.requestGroupProducts.getAnyLoading();
            },
            options() {
                return this.requestProductsTree.getData();
            },
        },
        methods: {
            getRequest(node) {
                if (node.isBranch) return this.requestGroupCategories;
                if (node.isLeaf) return this.requestGroupProducts;
                return null;
            },
            fetchProductsTreeSearchDebounced(searchQuery) {
                return new Promise((resolve, reject) => {
                    this.searchDebounced(searchQuery, resolve, reject);
                });
            },
            fetchProductsTree(categoryId) {
                return this.requestProductsTree.id(categoryId).get({ urlParams: { spaceId: this.spaceId }, requestParams: { category: categoryId } });
            },
            fetchProductsTreeSearch(searchQuery) {
                return this.requestProductsTreeSearch.get({ urlParams: { spaceId: this.spaceId }, requestParams: { search: searchQuery } });
            },
            loadOptions({ parentNode, searchQuery }) {
                if (searchQuery) {
                    return this.fetchProductsTreeSearchDebounced(searchQuery);
                }
                return this.fetchProductsTree(parentNode?.entityId);
            },
            normalizer(node) {
                return {
                    id: node.entityId,
                    label: node.text,
                    children: node.children === true ? null : node.children,
                };
            },
            handleClick(node) {
                if (node.isBranch) {
                    const childIds = node.children?.map(item => item.id) || [];
                    if (childIds.some(id => this.disabledNodes.includes(id))) {
                        this.$toastr.warning(this.$t('addCategoryChildExistError', { name: node.label }));
                        return;
                    }
                }
                if (node.isLeaf && node.parentNode?.id && this.disabledNodes.includes(node.parentNode.id)) {
                    this.$toastr.warning(this.$t('addProductCategoryExistError', { name: node.label }));
                    return;
                }
                const request = this.getRequest(node);
                if (!request) return;
                this.addItem(request, node.id).then(() => {
                    this.$emit('added', node);
                });
            },
            addItem(request, id) {
                return request
                    .id(`${this.groupId}-${id}`)
                    .post({ urlParams: { groupId: this.groupId, id } })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            getLoading(node) {
                const request = this.getRequest(node);
                return request ? request.getLoading(`${this.groupId}-${node.id}`) : false;
            },
            getDisabled(node) {
                return this.disabledNodes.includes(node.id);
            },
        },
        created() {
            this.searchDebounced = debounce((searchQuery, resolve, reject) => {
                this.fetchProductsTreeSearch(searchQuery)
                    .then(data => {
                        const tree = recursiveMapByKey(data, 'children', node => ({ ...node, children: node.items || false, isDefaultExpanded: true }));
                        resolve(tree);
                    })
                    .catch(reject);
            }, 500);
        },
    };
</script>
